import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import type React from 'react';
import { type MutableRefObject, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { company } from '~/company/Company';
import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import Icon from '~/components/Icon/Icon';
import { useMounted } from '~/hooks/useMounted';
import { useStores } from '~/hooks/useStore';
import { mainStore } from '~/stores';
import type { Category } from '~/stores/CategoriesStore';

import styles from './SuperMenu.module.scss';

interface SuperMenuProps {
  isOpen: boolean;
  onClose: () => void;
  buttonRef?: MutableRefObject<Nullable<HTMLButtonElement>>;
}

export const SuperMenu: React.FC<SuperMenuProps> = observer(
  ({ isOpen, onClose, buttonRef }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { shopCategories } = useStores();
    const menuRef = useRef<HTMLDivElement>(null);
    const [visibleCategoriesCount, setVisibleCategoriesCount] = useState(0);
    const mounted = useMounted();

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (!isOpen || !menuRef.current || !buttonRef?.current) {
          return;
        }

        const targetElement = event.target as HTMLElement;
        if (
          menuRef.current.contains(targetElement) ||
          buttonRef.current.contains(targetElement)
        ) {
          return;
        }

        onClose();
      };

      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          onClose();
        }
      };

      if (isOpen) {
        document.addEventListener('click', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
      }

      return () => {
        document.removeEventListener('click', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }, [isOpen, onClose]);

    useEffect(() => {
      const calculateVisibleCategories = () => {
        if (!menuRef.current || !isOpen) {
          return;
        }

        const menuWidth = menuRef.current.clientWidth;
        const menuPadding = 24 + 26;
        const rightSectionWidth = 200 + 16;
        const categoryWidth = 287;
        const categoryGap = 26;

        const availableWidth = menuWidth - menuPadding - rightSectionWidth;

        const maxCategories = Math.floor(
          (availableWidth + categoryGap) / (categoryWidth + categoryGap),
        );

        setVisibleCategoriesCount(Math.max(0, maxCategories));
      };

      calculateVisibleCategories();

      const handleResize = () => calculateVisibleCategories();
      window.addEventListener('resize', handleResize);

      return () => window.removeEventListener('resize', handleResize);
    }, [isOpen]);

    const handleCategoryClick = (categoryId: string | number) => {
      navigate(`/c/${categoryId}`);
      onClose();
    };

    const handleAllCategoriesClick = () => {
      navigate('/shop');
      onClose();
    };

    const getRightMenuCategories = () => {
      return shopCategories.list
        .reduce((acc: Category[], category: Category) => {
          return [...acc, ...category.subcategory];
        }, [])
        .filter((category) => category.showInSupermenu);
    };

    const rightMenuCategories = getRightMenuCategories();
    const visibleCategories = shopCategories.mainCategories.slice(
      0,
      visibleCategoriesCount,
    );

    const content = (
      <div
        className={classes([styles.backdrop, isOpen && styles.backdropVisible])}
        style={{
          top:
            mainStore.isDeliveryBannerShown &&
            company.showExpressDeliveryBannerMainPage
              ? 120
              : 80,
        }}
      >
        <div ref={menuRef} className={styles.superMenu}>
          <div className={styles.leftSection}>
            {visibleCategories.map((category) => (
              <div key={category.id} className={styles.categoryColumn}>
                <div className={styles.categoryIcon}>
                  <div className={styles.iconBackground}>
                    {category.previewImage && (
                      <CloudflareResponseImage
                        src={
                          category.previewImageThumb || category.previewImage
                        }
                        alt={category.name}
                        className={styles.categoryImage}
                        srcParams={{ width: 80, height: 80, fit: 'contain' }}
                        loading="lazy"
                      />
                    )}
                  </div>
                </div>
                <div className={styles.categoryInfo}>
                  <button
                    className={styles.categoryTitle}
                    onClick={() => handleCategoryClick(category.id)}
                  >
                    {category.name}
                  </button>
                  <div className={styles.subcategories}>
                    {category.subcategory.slice(0, 6).map((subcategory) => (
                      <button
                        key={subcategory.id}
                        className={styles.subcategoryItem}
                        onClick={() => handleCategoryClick(subcategory.id)}
                      >
                        {subcategory.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.rightSection}>
            <button
              className={styles.allCategoriesButton}
              onClick={handleAllCategoriesClick}
            >
              <div className={styles.rightMenuContent}>
                <Icon type="shop" size={16} />
                <span>{t('header:allCategories')}</span>
              </div>
              <Icon type="external" size={16} className={styles.externalIcon} />
            </button>

            {rightMenuCategories.map((category) => (
              <button
                key={category.id}
                className={styles.rightMenuItem}
                onClick={() => handleCategoryClick(category.id)}
              >
                <div className={styles.rightMenuContent}>
                  {category.icon && (
                    <img src={category.icon} className={styles.rightMenuIcon} />
                  )}
                  <span>{category.name}</span>
                </div>
                <Icon
                  type="external"
                  size={16}
                  className={styles.externalIcon}
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    );

    if (!mounted) {
      return null;
    }

    return createPortal(content, document.body);
  },
);
